import { randomUUID } from 'node:crypto';
import { Router } from 'express';
import { z } from 'zod';
import { requireAdmin, requireAuth, type AuthenticatedRequest } from './auth';
import { pool } from './db';
import { createSupportCallAdmission, liveKitReady } from './liveKitMedia';
import { notifyUsers } from './notifications';

export const supportRouter = Router();

const requestInput = z.object({
  topic: z.enum(['decision', 'technical', 'privacy', 'other']),
  message: z.string().trim().min(10).max(1200),
});
const responseInput = z.object({
  decision: z.enum(['accepted', 'reschedule']),
  note: z.string().trim().max(500).default(''),
});
const appointmentInput = z.object({ startsAt: z.iso.datetime({ offset: true }) });

const requestSelect = `
  SELECT support.id,support.user_id AS "userId",account.display_name AS "userName",
    account.username,account.email,support.topic,support.message,support.status,
    support.created_at AS "createdAt",support.updated_at AS "updatedAt",
    appointment.id AS "appointmentId",appointment.starts_at AS "appointmentStartsAt",
    appointment.status AS "appointmentStatus",appointment.user_note AS "appointmentNote"
  FROM support_requests support
  JOIN users account ON account.id=support.user_id
  LEFT JOIN LATERAL (
    SELECT * FROM support_appointments item WHERE item.support_request_id=support.id
    ORDER BY item.created_at DESC LIMIT 1
  ) appointment ON TRUE`;

supportRouter.get('/v1/me/onboarding', requireAuth, async (request: AuthenticatedRequest, response) => {
  const result = await pool.query<{ onboarding_seen_at: Date | null }>(
    'SELECT onboarding_seen_at FROM users WHERE id=$1', [request.authUser!.id],
  );
  response.json({ show: !result.rows[0]?.onboarding_seen_at });
});

supportRouter.post('/v1/me/onboarding/dismiss', requireAuth,
  async (request: AuthenticatedRequest, response) => {
    await pool.query('UPDATE users SET onboarding_seen_at=COALESCE(onboarding_seen_at,NOW()) WHERE id=$1',
      [request.authUser!.id]);
    response.json({ dismissed: true });
  });

supportRouter.get('/v1/me/support', requireAuth, async (request: AuthenticatedRequest, response) => {
  const result = await pool.query(`${requestSelect} WHERE support.user_id=$1
    ORDER BY support.created_at DESC LIMIT 20`, [request.authUser!.id]);
  response.json({ requests: result.rows, mediaConfigured: liveKitReady() });
});

supportRouter.post('/v1/me/support', requireAuth, async (request: AuthenticatedRequest, response) => {
  const parsed = requestInput.safeParse(request.body);
  if (!parsed.success) { response.status(400).json({ error: 'Invalid support request' }); return; }
  try {
    const id = randomUUID();
    await pool.query(
      `INSERT INTO support_requests (id,user_id,topic,message) VALUES ($1,$2,$3,$4)`,
      [id, request.authUser!.id, parsed.data.topic, parsed.data.message],
    );
    await pool.query(
      `INSERT INTO audit_events (user_id,event_type,details) VALUES ($1,'support_requested',$2::jsonb)`,
      [request.authUser!.id, JSON.stringify({ supportRequestId: id, topic: parsed.data.topic })],
    );
    response.status(201).json({ id });
  } catch (error) {
    if ((error as { code?: string }).code === '23505') {
      response.status(409).json({ error: 'An open support request already exists' }); return;
    }
    throw error;
  }
});

supportRouter.post('/v1/me/support/:id/respond', requireAuth,
  async (request: AuthenticatedRequest, response) => {
    const id = z.uuid().safeParse(request.params.id);
    const body = responseInput.safeParse(request.body);
    if (!id.success || !body.success) { response.status(400).json({ error: 'Invalid response' }); return; }
    const appointment = await pool.query<{ id: string }>(
      `SELECT appointment.id FROM support_appointments appointment
       JOIN support_requests support ON support.id=appointment.support_request_id
       WHERE support.id=$1 AND support.user_id=$2 AND appointment.status='proposed'
       ORDER BY appointment.created_at DESC LIMIT 1`, [id.data, request.authUser!.id],
    );
    if (!appointment.rows[0]) { response.status(409).json({ error: 'No proposed appointment' }); return; }
    const nextStatus = body.data.decision === 'accepted' ? 'accepted' : 'reschedule_requested';
    const client = await pool.connect();
    try {
      await client.query('BEGIN');
      await client.query(
        `UPDATE support_appointments SET status=$1,user_note=$2,updated_at=NOW() WHERE id=$3`,
        [nextStatus, body.data.note, appointment.rows[0].id],
      );
      await client.query('UPDATE support_requests SET status=$1,updated_at=NOW() WHERE id=$2',
        [nextStatus, id.data]);
      await client.query('COMMIT');
    } catch (error) { await client.query('ROLLBACK'); throw error; }
    finally { client.release(); }
    response.json({ status: nextStatus });
  });

const joinSupportCall = async (request: AuthenticatedRequest, response: import('express').Response,
  admin: boolean) => {
  const id = z.uuid().safeParse(request.params.id);
  if (!id.success) { response.status(400).json({ error: 'Invalid support request' }); return; }
  const result = await pool.query<{ status: string; user_id: string }>(
    `SELECT status,user_id FROM support_requests WHERE id=$1`, [id.data],
  );
  const item = result.rows[0];
  if (!item || (!admin && item.user_id !== request.authUser!.id)) {
    response.status(404).json({ error: 'Support request not found' }); return;
  }
  if (item.status !== 'accepted') {
    response.status(409).json({ error: 'Support appointment is not accepted' }); return;
  }
  response.json(await createSupportCallAdmission({
    requestId: id.data, userId: request.authUser!.id,
    displayName: request.authUser!.displayName, role: admin ? 'admin' : 'user',
  }));
};

supportRouter.post('/v1/me/support/:id/join', requireAuth,
  (request: AuthenticatedRequest, response) => void joinSupportCall(request, response, false));

supportRouter.get('/admin/support', requireAuth, requireAdmin, async (_request, response) => {
  const result = await pool.query(`${requestSelect} ORDER BY
    CASE support.status WHEN 'reschedule_requested' THEN 0 WHEN 'requested' THEN 1
      WHEN 'scheduled' THEN 2 WHEN 'accepted' THEN 3 ELSE 4 END, support.updated_at DESC`);
  response.json({ requests: result.rows, mediaConfigured: liveKitReady() });
});

supportRouter.post('/admin/support/:id/appointment', requireAuth, requireAdmin,
  async (request: AuthenticatedRequest, response) => {
    const id = z.uuid().safeParse(request.params.id);
    const body = appointmentInput.safeParse(request.body);
    if (!id.success || !body.success || new Date(body.data.startsAt).getTime() <= Date.now()) {
      response.status(400).json({ error: 'Invalid appointment' }); return;
    }
    const owner = await pool.query<{ user_id: string }>(
      `SELECT user_id FROM support_requests WHERE id=$1 AND status NOT IN ('completed','cancelled')`, [id.data],
    );
    if (!owner.rows[0]) { response.status(404).json({ error: 'Support request not found' }); return; }
    const appointmentId = randomUUID();
    const client = await pool.connect();
    try {
      await client.query('BEGIN');
      await client.query(
        `UPDATE support_appointments SET status='superseded',updated_at=NOW()
         WHERE support_request_id=$1 AND status IN ('proposed','accepted','reschedule_requested')`, [id.data],
      );
      await client.query(
        `INSERT INTO support_appointments (id,support_request_id,proposed_by,starts_at)
         VALUES ($1,$2,$3,$4)`, [appointmentId, id.data, request.authUser!.id, body.data.startsAt],
      );
      await client.query("UPDATE support_requests SET status='scheduled',updated_at=NOW() WHERE id=$1", [id.data]);
      await client.query('COMMIT');
    } catch (error) { await client.query('ROLLBACK'); throw error; }
    finally { client.release(); }
    await notifyUsers([owner.rows[0].user_id], {
      type: 'support_appointment', title: 'موعد دعم جديد',
      body: 'اقترح فريق الدعم موعدًا لمكالمة صوتية. افتح الطلب لعرضه حسب توقيت جهازك.',
      supportRequestId: id.data, dedupeKey: `support-appointment:${appointmentId}`,
    });
    response.status(201).json({ appointmentId });
  });

supportRouter.post('/admin/support/:id/join', requireAuth, requireAdmin,
  (request: AuthenticatedRequest, response) => void joinSupportCall(request, response, true));

supportRouter.post('/admin/support/:id/complete', requireAuth, requireAdmin,
  async (request: AuthenticatedRequest, response) => {
    const id = z.uuid().safeParse(request.params.id);
    if (!id.success) { response.status(400).json({ error: 'Invalid support request' }); return; }
    const result = await pool.query(
      `UPDATE support_requests SET status='completed',updated_at=NOW() WHERE id=$1 RETURNING user_id`, [id.data],
    );
    if (!result.rowCount) { response.status(404).json({ error: 'Support request not found' }); return; }
    await pool.query(
      `UPDATE support_appointments SET status='completed',updated_at=NOW()
       WHERE support_request_id=$1 AND status='accepted'`, [id.data],
    );
    await notifyUsers([result.rows[0].user_id], {
      type: 'support_updated', title: 'اكتملت متابعة الدعم',
      body: 'أغلق فريق الدعم الطلب بعد اكتمال المساعدة. يمكنك إنشاء طلب جديد عند الحاجة.',
      supportRequestId: id.data, dedupeKey: `support-completed:${id.data}`,
    });
    response.json({ completed: true });
  });
